import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import {
  EXPAND_DURATION_MS,
  EXPAND_SCALE,
  REVEAL_FADE_MS,
  WHITE_FADE_MS,
} from './readyStep.constants';
import { glowColorFor } from './RevealBackground';

/** Label font-size targets per phase (px). Animated directly on
 *  fontSize so the glyphs are rasterised at their final size on
 *  every frame — using CSS `transform: scale` on text always
 *  rasterises at the original size and stretches the bitmap, which
 *  produced a visibly pixelated "GRAIL" at scale=12. */
const LABEL_FONT_SIZE_REVEAL_PX = 36;
const LABEL_FONT_SIZE_EXPAND_PX = 440;
/** What fraction of the expand tween the label stays at full
 *  opacity before fading out. `0.55` = bright for the first 55 %
 *  of the swell, then begins fading. */
const LABEL_HOLD_FRACTION = 0.55;
/** What fraction of the expand tween the label fade-out occupies
 *  (right after the hold). `0.35` = fade runs from 55 % → 90 %
 *  of the timeline, leaving a clean ~10 % at zero before expand
 *  completes. */
const LABEL_FADE_OUT_FRACTION = 0.35;

export type GlowPhase = 'reveal' | 'expand' | 'fade';

interface RevealGlowOverlayProps {
  /**
   *  `reveal` — steady glow plate, no scaling.
   *  `expand` — same plate scales up to fill the viewport.
   *  `fade`   — already-expanded plate fades to opacity 0.
   */
  phase: GlowPhase;
  badge: string | null;
  /** Whether the badge label ("GRAIL") is visible on the plate.
   *  Defaults to `true`. The parent flips this to `false` once the
   *  character / grade text sequence mounts, so the huge label
   *  doesn't fight with the foreground text. */
  showLabel?: boolean;
  /** Fires once the `expand` tween completes. Parent uses it to
   *  swap status to winner and mount the badge text sequence. */
  onExpandComplete?: () => void;
  /** Fires once the `fade` tween completes. Parent uses it to
   *  unmount this overlay entirely. */
  onFadeComplete?: () => void;
}

/**
 * Unified reveal-screen glow. One mounted node owns BOTH the steady
 * "white plate with GRAIL on it" state AND the scale-up-to-full-screen
 * wipe — the original code split these across `<RevealGlowSquare>`
 * (inside the slot-lane viewport) and `<RevealWhiteWipe>` (on
 * PackReveal root), and the handoff between them read as a "new
 * element appeared" pop because the second mount started at a
 * slightly different Y position.
 *
 * Now a single motion.div ramps `scale` and `opacity` based on the
 * phase prop, so the plate the user sees during `reveal` IS the same
 * plate that swells into the viewport on `expand`.
 *
 * Bottom padding (`pb-[88px]`) compensates for the Shuffle-CTA gutter
 * underneath ReadyStep so the glow centres at the same Y the
 * old `<RevealGlowSquare>` did (CTA wrap = pb-6 24px + h-16 64px =
 * 88px). Without this the glow sits ~44 px lower than the original
 * pack-tile centre.
 */
export function RevealGlowOverlay({
  phase,
  badge,
  showLabel = true,
  onExpandComplete,
  onFadeComplete,
}: RevealGlowOverlayProps) {
  // Timer-driven completion for the expand tween — the parent gates
  // mounting the badge text sequence on this signal (we want the
  // overlay fully expanded before the character / grade text plays
  // on top).
  const [expandDone, setExpandDone] = useState(false);
  useEffect(() => {
    if (phase !== 'expand' || expandDone) return;
    const t = window.setTimeout(() => {
      setExpandDone(true);
      onExpandComplete?.();
    }, EXPAND_DURATION_MS);
    return () => window.clearTimeout(t);
  }, [phase, expandDone, onExpandComplete]);

  // Label opacity is driven as a single numeric target, NOT a
  // motion keyframe array. Keyframe arrays re-play from frame[0]
  // every time `animate` changes identity, which caused the label
  // to flash back to opacity 1 the moment the parent re-rendered
  // mid-expand (e.g. mounting BadgeRevealSequence). One numeric
  // target + a setTimeout for the hold-then-fade window keeps the
  // tween monotonic.
  const [labelOpacityTarget, setLabelOpacityTarget] = useState(0);
  useEffect(() => {
    if (phase === 'reveal') {
      setLabelOpacityTarget(1);
      return;
    }
    if (phase === 'expand') {
      // Hold at 1 for the first chunk of the swell, then drop to 0
      // so the text is gone well before the plate finishes
      // expanding. Timer fires once per phase entry — no flash on
      // subsequent parent re-renders.
      setLabelOpacityTarget(1);
      const t = window.setTimeout(
        () => setLabelOpacityTarget(0),
        EXPAND_DURATION_MS * LABEL_HOLD_FRACTION,
      );
      return () => window.clearTimeout(t);
    }
    // phase === 'fade': already 0 from end of expand; keep 0.
    setLabelOpacityTarget(0);
  }, [phase]);

  // Resolve animation target + duration from the current phase.
  const targetScale = phase === 'reveal' ? 1 : EXPAND_SCALE;
  const targetOpacity = phase === 'fade' ? 0 : 1;
  const duration =
    phase === 'reveal'
      ? REVEAL_FADE_MS
      : phase === 'expand'
        ? EXPAND_DURATION_MS
        : WHITE_FADE_MS;

  const labelColor = badge
    ? glowColorFor(badge)
    : 'var(--ui-badge-default, #FFFFFF)';
  const labelFontSize =
    phase === 'reveal'
      ? LABEL_FONT_SIZE_REVEAL_PX
      : LABEL_FONT_SIZE_EXPAND_PX;
  // Opacity transition duration depends on which way we're moving.
  // In `expand` it's the fade-out window (≈ 35 % of the expand
  // tween). For reveal / fade just match the plate's transition.
  const labelOpacityDurationMs =
    phase === 'expand'
      ? EXPAND_DURATION_MS * LABEL_FADE_OUT_FRACTION
      : duration;
  // Force the label hidden the moment `showLabel` flips false even
  // if the timeline target was still 1 — guards against any race.
  const effectiveLabelOpacity = showLabel ? labelOpacityTarget : 0;

  return (
    <div
      aria-hidden
      className="
        pointer-events-none absolute inset-0 z-30
        flex items-center justify-center pb-[88px]
      "
    >
      {/* Blurred white plate. Scales via CSS `transform: scale`,
          which is fine for a uniform blurred rectangle (no glyphs
          to rasterise). Lives in its own absolute container so the
          label below can be a sibling (not a child of the scaling
          transform — text inside scaling transforms always reads
          as pixelated bitmap).

          Width tracks the slot-tile sizing in `SlotTile.tsx`
          (260/280/360/420) at ~80 % so the glow reads as "inside
          the tile" instead of dwarfed by it. Old RevealGlowSquare
          had the same per-breakpoint ladder; when this was
          flattened into the wipe earlier the responsive widths
          got dropped and the glow shrank on wide viewports. */}
      <motion.div
        className="
          absolute bg-white rounded-[24px]
          w-[210px] @[500px]:w-[224px] @[800px]:w-[290px] @[1100px]:w-[336px]
        "
        style={{
          aspectRatio: '421 / 526',
          filter: 'blur(24px)',
          willChange: 'transform, opacity',
        }}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: targetOpacity, scale: targetScale }}
        transition={{ duration: duration / 1000, ease: 'easeOut' }}
        onAnimationComplete={() => {
          if (phase === 'fade') onFadeComplete?.();
        }}
      />

      {/* Badge label — animates `fontSize` directly instead of riding
          a scaling transform, so the glyphs render at their target
          size on every frame and stay crisp (no rasterise-and-stretch
          pixelation). Centred via the parent flex layout, so it
          sits exactly over the plate's centre. `showLabel=false`
          fades the label out before the character / grade sequence
          plays, so the giant title doesn't compete with foreground
          text — Figma 741:62374. */}
      {badge && (
        <motion.span
          className="absolute uppercase font-bold tracking-[0.5px] [font-family:Inter,sans-serif]"
          style={{
            color: labelColor,
            // `${labelColor}80` (hex+alpha) doesn't work now that
            // `labelColor` is a `var(--ui-badge-…)` expression — wrap
            // it in `color-mix` instead.
            textShadow: `0 0 24px color-mix(in srgb, ${labelColor} 50%, transparent)`,
            whiteSpace: 'nowrap',
            lineHeight: 1,
          }}
          initial={{
            opacity: 0,
            fontSize: LABEL_FONT_SIZE_REVEAL_PX,
          }}
          animate={{
            opacity: effectiveLabelOpacity,
            fontSize: labelFontSize,
          }}
          transition={{
            fontSize: { duration: duration / 1000, ease: 'easeOut' },
            opacity: {
              duration: labelOpacityDurationMs / 1000,
              ease: 'easeOut',
            },
          }}
        >
          {badge}
        </motion.span>
      )}
    </div>
  );
}
